import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Stack, TextField, Typography
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';

// Pippaya - Privacyversie 27-09-2026.
// Lezen gaat via een RPC met een verplichte server-side admincontrole.
// Opslaan blijft via de al bestaande admin_update_dog_settings-functie.
// Geen service-role-sleutel toevoegen aan dit bestand of aan de browser.

type Family = {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
};

type Dog = {
  id: string;
  name: string;
  breed: string | null;
  sex: string;
  birth_date: string | null;
  neutered: boolean | null;
  chemically_neutered: boolean | null;
  direct_booking_allowed: boolean;
  capacity_exception: boolean;
  off_leash_allowed: boolean;
  swimming_allowed: boolean;
  active: boolean;
  care_notes: string | null;
  private_notes: string | null;
  families: Family | null;
};

type Filter = 'active' | 'inactive' | 'all';
type Settings = {
  direct_booking_allowed: boolean;
  capacity_exception: boolean;
  off_leash_allowed: boolean;
  swimming_allowed: boolean;
  active: boolean;
  private_notes: string;
};
type Flag = Exclude<keyof Settings, 'private_notes'>;

const CHECKBOXES: { key: Flag; label: string; help?: string }[] = [
  { key: 'direct_booking_allowed', label: 'Direct boeken toegestaan' },
  {
    key: 'capacity_exception', label: 'Capaciteitsuitzondering',
    help: 'Alleen inschakelen als voor deze hond bewust een uitzondering op de normale bezetting geldt.'
  },
  { key: 'off_leash_allowed', label: 'Loslopen toegestaan' },
  { key: 'swimming_allowed', label: 'Zwemmen toegestaan' },
  { key: 'active', label: 'Actieve hond' }
];

function errorText(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return fallback;
}

function readDogs(value: unknown): Dog[] {
  if (!Array.isArray(value)) {
    throw new Error('De beheer-leesfunctie gaf geen geldige hondenlijst terug.');
  }
  // Controleer de RPC-vorm voordat de interface deze gaat gebruiken.
  for (const row of value) {
    if (!row || typeof row !== 'object' || typeof row.id !== 'string' ||
        typeof row.name !== 'string' || typeof row.sex !== 'string') {
      throw new Error('Een hond heeft onverwachte gegevens in de beheer-leesfunctie.');
    }
    for (const key of ['direct_booking_allowed', 'capacity_exception',
      'off_leash_allowed', 'swimming_allowed', 'active']) {
      if (typeof row[key] !== 'boolean') {
        throw new Error('Een beheerinstelling ontbreekt in de opgehaalde hondenlijst.');
      }
    }
    for (const key of ['breed', 'birth_date', 'care_notes', 'private_notes']) {
      if (row[key] !== null && typeof row[key] !== 'string') {
        throw new Error('Een tekst- of datumveld ontbreekt in de opgehaalde hondenlijst.');
      }
    }
    for (const key of ['neutered', 'chemically_neutered']) {
      if (row[key] !== null && typeof row[key] !== 'boolean') {
        throw new Error('Een hondgegeven heeft een onverwacht formaat.');
      }
    }
    if (row.families !== null) {
      if (!row.families || typeof row.families !== 'object' || Array.isArray(row.families)) {
        throw new Error('De klantgegevens hebben een onverwacht formaat.');
      }
      for (const key of ['first_name', 'last_name', 'email', 'phone']) {
        if (row.families[key] !== null && typeof row.families[key] !== 'string') {
          throw new Error('Een klantgegeven heeft een onverwacht formaat.');
        }
      }
    }
  }
  return value as Dog[];
}

function dogDate(value: string | null): string {
  if (!value) return 'Niet ingevuld';
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return 'Datum niet beschikbaar';
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric'
  }).format(parsed);
}

const yesNo = (value: boolean | null) =>
  value === null ? 'Niet ingevuld' : value ? 'Ja' : 'Nee';

export const AdminDogsPage = () => {
  const navigate = useNavigate();
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('active');
  const [edit, setEdit] = useState<Dog | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const alive = useRef(false);
  const requestNumber = useRef(0);
  const saveLock = useRef(false);

  const loadDogs = useCallback(async (): Promise<boolean> => {
    const request = ++requestNumber.current;
    if (alive.current) {
      setRefreshing(true);
      setError('');
    }
    try {
      if (!supabase) throw new Error('De verbinding met Supabase is niet ingesteld.');
      // Niet terugvallen op .from('dogs').select('...private_notes...').
      const response = await supabase.rpc('admin_list_dogs_private_v1');
      if (response.error) throw response.error;
      const rows = readDogs(response.data);
      if (!alive.current || request !== requestNumber.current) return false;
      setDogs(rows);
      return true;
    } catch (err) {
      if (alive.current && request === requestNumber.current) {
        setDogs([]);
        setError(errorText(err, 'De hondgegevens konden niet worden geladen.'));
      }
      return false;
    } finally {
      if (alive.current && request === requestNumber.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void loadDogs();
    return () => {
      alive.current = false;
      requestNumber.current += 1;
    };
  }, [loadDogs]);

  const visibleDogs = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('nl-NL');
    return dogs.filter(dog => {
      if (filter === 'active' && !dog.active) return false;
      if (filter === 'inactive' && dog.active) return false;
      const text = [dog.name, dog.breed, dog.families?.first_name,
        dog.families?.last_name, dog.families?.email, dog.families?.phone]
        .filter(Boolean).join(' ').toLocaleLowerCase('nl-NL');
      return !query || text.includes(query);
    });
  }, [dogs, search, filter]);

  function openEdit(dog: Dog) {
    setEdit(dog);
    setSettings({
      direct_booking_allowed: dog.direct_booking_allowed,
      capacity_exception: dog.capacity_exception,
      off_leash_allowed: dog.off_leash_allowed,
      swimming_allowed: dog.swimming_allowed,
      active: dog.active,
      private_notes: dog.private_notes ?? ''
    });
    setDialogError('');
    setSuccess('');
  }

  function closeEdit() {
    if (saveLock.current) return;
    setEdit(null);
    setSettings(null);
    setDialogError('');
  }

  async function saveSettings() {
    if (!edit || !settings || saveLock.current) return;
    const dogId = edit.id;
    const draft = { ...settings };
    saveLock.current = true;
    setSaving(true);
    setDialogError('');
    setSuccess('');
    try {
      if (!supabase) throw new Error('De verbinding met Supabase is niet ingesteld.');
      const response = await supabase.rpc('admin_update_dog_settings', {
        p_dog_id: dogId,
        p_direct_booking_allowed: draft.direct_booking_allowed,
        p_capacity_exception: draft.capacity_exception,
        p_off_leash_allowed: draft.off_leash_allowed,
        p_swimming_allowed: draft.swimming_allowed,
        p_active: draft.active,
        p_private_notes: draft.private_notes
      });
      if (response.error) throw response.error;
      if (!Array.isArray(response.data) || response.data[0]?.dog_id !== dogId) {
        throw new Error('Geen duidelijke opslagbevestiging ontvangen. Ververs de pagina om de opgeslagen instellingen te controleren.');
      }
      if (!alive.current) return;
      setEdit(null);
      setSettings(null);
      const reloaded = await loadDogs();
      if (alive.current) {
        setSuccess(reloaded
          ? 'De instellingen van de hond zijn opgeslagen.'
          : 'De instellingen zijn opgeslagen, maar het opnieuw laden lukte niet. Klik op Vernieuwen.');
      }
    } catch (err) {
      if (alive.current) {
        setDialogError(errorText(err, 'De instellingen konden niet worden opgeslagen.'));
      }
    } finally {
      saveLock.current = false;
      if (alive.current) setSaving(false);
    }
  }

  const activeCount = dogs.filter(dog => dog.active).length;
  if (loading) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffafb', py: 4 }}>
      <Container maxWidth="lg">
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/beheer')}>
          Terug naar beheer
        </Button>
        <Typography variant="overline" color="text.secondary">Pippaya beheer</Typography>
        <Typography variant="h3">Honden</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Bekijk alle honden en beheer de instellingen voor de opvang.
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>}

        <Card sx={{ p: 2, mt: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField fullWidth label="Zoeken" value={search}
              placeholder="Zoek op hond, ras, klant, e-mail of telefoonnummer"
              onChange={event => setSearch(event.target.value)} />
            <TextField select label="Weergave" value={filter} sx={{ minWidth: 220 }}
              onChange={event => setFilter(event.target.value as Filter)}>
              <MenuItem value="active">Actieve honden ({activeCount})</MenuItem>
              <MenuItem value="inactive">Inactieve honden ({dogs.length - activeCount})</MenuItem>
              <MenuItem value="all">Alle honden ({dogs.length})</MenuItem>
            </TextField>
            <Button variant="outlined" disabled={refreshing || saving} onClick={() => void loadDogs()}>
              {refreshing ? 'Laden...' : 'Vernieuwen'}
            </Button>
          </Stack>
        </Card>

        {!error && (
          <>
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {visibleDogs.length} {visibleDogs.length === 1 ? 'hond' : 'honden'} gevonden
            </Typography>
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              {visibleDogs.map(dog => {
                const customer = [dog.families?.first_name, dog.families?.last_name]
                  .filter(Boolean).join(' ');
                return (
                  <Card key={dog.id} sx={{ p: 2.5 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                      <Stack direction="row" spacing={2}>
                        <PetsRoundedIcon />
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h5">{dog.name}</Typography>
                            <Chip size="small" color={dog.active ? 'success' : 'default'}
                              label={dog.active ? 'Actief' : 'Inactief'} />
                            {dog.capacity_exception && <Chip size="small" color="warning" label="Capaciteitsuitzondering" />}
                          </Stack>
                          <Typography color="text.secondary">{dog.breed || 'Ras niet ingevuld'}</Typography>
                        </Box>
                      </Stack>
                      <Button startIcon={<EditRoundedIcon />} onClick={() => openEdit(dog)}
                        disabled={refreshing || saving} sx={{ alignSelf: 'flex-start' }}>
                        Beheer instellingen
                      </Button>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={5} sx={{ mt: 2 }}>
                      <Box flex={1} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        <Typography fontWeight={800}>Klant</Typography>
                        <Typography>{customer || 'Naam niet ingevuld'}</Typography>
                        <Typography>{dog.families?.email || 'Geen e-mailadres'}</Typography>
                        <Typography>{dog.families?.phone || 'Geen telefoonnummer'}</Typography>
                      </Box>
                      <Box flex={1}>
                        <Typography fontWeight={800}>Hondgegevens</Typography>
                        <Typography>Geslacht: {dog.sex}</Typography>
                        <Typography>Geboortedatum: {dogDate(dog.birth_date)}</Typography>
                        <Typography>Gecastreerd/gesteriliseerd: {yesNo(dog.neutered)}</Typography>
                        <Typography>Chemisch gecastreerd: {yesNo(dog.chemically_neutered)}</Typography>
                      </Box>
                    </Stack>

                    {dog.care_notes && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <strong>Verzorging & bijzonderheden</strong>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {dog.care_notes}
                        </Typography>
                      </Alert>
                    )}
                    {dog.private_notes && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <strong>Interne notities</strong>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {dog.private_notes}
                        </Typography>
                      </Alert>
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                      <Chip size="small" color={dog.direct_booking_allowed ? 'success' : 'default'}
                        label={dog.direct_booking_allowed ? 'Direct boeken' : 'Boeken na overleg'} />
                      <Chip size="small" label={dog.off_leash_allowed ? 'Loslopen toegestaan' : 'Niet loslopen'} />
                      <Chip size="small" label={dog.swimming_allowed ? 'Zwemmen toegestaan' : 'Niet zwemmen'} />
                    </Stack>
                  </Card>
                );
              })}
              {visibleDogs.length === 0 && (
                <Alert severity="info">Geen honden gevonden met deze zoekopdracht of dit filter.</Alert>
              )}
            </Stack>
          </>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3 }}>
          Privacyversie 27-09-2026 · beheer-leesfunctie
        </Typography>
      </Container>

      <Dialog open={!!edit} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Instellingen hond beheren</DialogTitle>
        <DialogContent>
          {edit && settings && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Alert severity="info">
                Instellingen voor <strong>{edit.name}</strong>. Deze instellingen worden alleen door Pippaya beheerd.
              </Alert>
              {dialogError && <Alert severity="error">{dialogError}</Alert>}
              {CHECKBOXES.map(item => (
                <Box key={item.key}>
                  <FormControlLabel label={item.label} control={
                    <Checkbox checked={settings[item.key]} disabled={saving}
                      onChange={event => {
                        const checked = event.target.checked;
                        setSettings(current => current ? { ...current, [item.key]: checked } : current);
                      }} />
                  } />
                  {item.help && <Typography variant="caption" color="text.secondary" display="block">{item.help}</Typography>}
                </Box>
              ))}
              <TextField label="Interne notities" value={settings.private_notes} disabled={saving}
                onChange={event => {
                  const value = event.target.value;
                  setSettings(current => current ? { ...current, private_notes: value } : current);
                }}
                multiline minRows={4} helperText="Interne informatie voor Pippaya-beheer." />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={closeEdit}>Annuleren</Button>
          <Button variant="contained" disabled={saving || !edit || !settings} onClick={() => void saveSettings()}>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
